import { iti, initializeIntlTelInput } from "./tel_input";
import { displaySpinner, hideSpinner } from "./packs/application";

document.addEventListener("turbo:load", initializePaymentForms);
document.addEventListener("reinitializePaymentForms", initializePaymentForms);

/**
 * Initializes the payment forms on page load.
 * This function is called when the page is loaded or when the payment forms are re-initialized.
 * It sets up the mobile money operator select, initializes the mobile money form,
 * and initializes the credit card form.
 * It also sets up event listeners for form submission and AJAX events.
 */
function initializePaymentForms() {
  const mode = document.querySelector(
    'input[name="payment_form[order][payment_mode]"]',
  )?.value;

  if (mode === "installment") {
    initializeInstallmentForm();
    return; // NOTE: Skip payment processing as it should happen at a later stage.
  }

  const operatorSelect = document.querySelector("#mobile-money-operator");
  if (operatorSelect) setupMobileMoneyOperatorSelect(operatorSelect);

  const mobileMoneyForm = document.querySelector("#charge-mobile-money-form");
  if (mobileMoneyForm) initializeMobileMoneyForm(mobileMoneyForm);

  const creditCardForm = document.querySelector("#charge-credit-card-form");
  if (creditCardForm) {
    const cardElementContainer = document.getElementById("card-element");
    if (cardElementContainer) initializeCreditCardForm(creditCardForm);
  }
}

/**
 * Sets up the mobile money operator select element.
 * Attaches an event listener to update the country flag based on the selected operator.
 *
 * @param {HTMLElement} operatorSelect - The mobile money operator select element.
 */
function setupMobileMoneyOperatorSelect(operatorSelect) {
  const operatorMapping = getOperatorMapping();

  operatorSelect.addEventListener("change", (event) => {
    const selectedOperator = event.target.value;
    updateFlagByOperator(selectedOperator, operatorMapping);

    const form = document.querySelector("#charge-mobile-money-form");
    if (form) {
      const { countryCode } = operatorMapping[selectedOperator];
      form.querySelector('input[name="payment_form[order][country]"]').value =
        countryCode;

      const paymentGateway = choosePaymentGateway(form, countryCode);
      form.querySelector(
        'input[name="payment_form[order][payment_gateway]"]',
      ).value = paymentGateway;
    }
  });
}

/**
 * Initializes the Mobile Money form by attaching event handlers for form submission
 * and handling AJAX events.
 *
 * @param {HTMLElement} form - The mobile money form element.
 */
function initializeMobileMoneyForm(form) {
  initializeIntlTelInput();

  const submitButton = form.querySelector("#mobile-money-submit-button");
  const spinner = submitButton.querySelector(".spinner-border");

  form.addEventListener("ajax:beforeSend", (_event) => {
    if (spinner) displaySpinner(spinner);
    if (submitButton) submitButton.disabled = true;
  });

  form.addEventListener("ajax:success", (event) => {
    hideSpinner(spinner);
    const [data, _status, _xhr] = event.detail;

    if (data.status === "success") {
      if (spinner) hideSpinner(spinner);
      submitButton.disabled = false;
      Turbo.visit(data.redirectionUrl);
    } else if (data.status == "pending") {
      const paymentGateway = form.querySelector(
        'input[name="payment_form[order][payment_gateway]"]',
      ).value;

      if (paymentGateway === "fedapay") handleFedapayPayment(data);
      else if (data.redirectUrl) window.location = data.redirectUrl;
    } else if (data.status == "requires_action" && data.redirectUrl) {
      Turbo.visit(data.redirectUrl);
    } else {
      flashAlert("Erreur de confirmation de paiement : " + data.error);
      if (spinner) hideSpinner(spinner);
      submitButton.disabled = false;
    }
  });

  form.addEventListener("ajax:error", (event) => {
    const [data, _status, _xhr] = event.detail;
    flashAlert("Erreur de confirmation de paiement : " + data.error);
    if (spinner) hideSpinner(spinner);
    submitButton.disabled = false;
  });
}

/**
 * Handles Fedapay payment process by initializing the Fedapay widget.
 *
 * @param {Object} result - The payment result containing transaction info.
 */
function handleFedapayPayment(result) {
  const fedapayPublicKey = document.querySelector(
    "meta[name='fedapay-public-key']",
  ).content;

  if (!window.FedaPay) return;

  const fedapay_widget = FedaPay.init({
    public_key: fedapayPublicKey,
    transaction: {
      id: result.transactionId,
    },
  });

  fedapay_widget.open();
}

/**
 * Returns a mapping of mobile money operators to their country codes and prefixes.
 *
 * @returns {Object} operatorMapping - An object mapping operator keys to their data.
 */
function getOperatorMapping() {
  const form = document.querySelector("#charge-mobile-money-form");
  if (!form) return {};

  const operatorMappingData = JSON.parse(form.dataset.operatorMapping);
  const countryPrefixMapping = JSON.parse(form.dataset.countryPrefixMapping);

  const operatorMapping = {};
  for (const [countryCode, operators] of Object.entries(operatorMappingData)) {
    const prefix = countryPrefixMapping[countryCode];
    operators.forEach((operator) => {
      const operatorKey = `${operator.toLowerCase()}_${countryCode.toLowerCase()}`;
      operatorMapping[operatorKey] = { countryCode, prefix };
    });
  }

  return operatorMapping;
}

/**
 * Updates the displayed country flag based on the selected mobile money operator.
 *
 * @param {String} operator - The selected operator key.
 * @param {Object} operatorMapping - The mapping object from getOperatorMapping().
 */
function updateFlagByOperator(operator, operatorMapping) {
  const countryCode = operatorMapping[operator].countryCode;
  iti.setCountry(countryCode);
}

/**
 * Chooses the appropriate payment gateway based on the country code.
 *
 * @param {HTMLElement} form - The form element containing data attributes.
 * @param {String} countryCode - The determined country code.
 * @returns {String} paymentGateway - The chosen payment gateway.
 */
function choosePaymentGateway(form, countryCode) {
  const pawapaySupportedCountries = JSON.parse(
    form.dataset.pawapaySupportedCountries,
  );
  const fedapaySupportedCountries = JSON.parse(
    form.dataset.fedapaySupportedCountries,
  );

  if (pawapaySupportedCountries.includes(countryCode)) {
    return "pawapay";
  } else if (fedapaySupportedCountries.includes(countryCode)) {
    return "fedapay";
  } else {
    return "pawapay";
  }
}

/**
 * Initializes the credit card form with Stripe elements.
 * Handles the click event on the submit button to create a PaymentMethod,
 * and then submits the form via Rails UJS.
 *
 * @param {HTMLElement} form - The credit card form element.
 */
async function initializeCreditCardForm(form) {
  const { stripe, cardElement } = initializeStripeElements(form);
  const submitButton = form.querySelector("#credit-card-submit-button");
  const spinner = submitButton.querySelector(".spinner-border");

  submitButton.addEventListener("click", async (e) => {
    e.preventDefault();

    if (spinner) displaySpinner(spinner);
    submitButton.disabled = true;

    const cardHolderName = form.querySelector("#card_holder_name").value;

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        name: cardHolderName,
      },
    });

    if (error) {
      flashAlert("Erreur dans le mode de paiement : " + error.message);
      if (spinner) hideSpinner(spinner);
      submitButton.disabled = false;
    } else {
      form.querySelector(
        'input[name="payment_form[order][payment_gateway]"]',
      ).value = "stripe";
      form.querySelector(
        'input[name="payment_form[order][payment_method]"]',
      ).value = "credit-card";
      form.querySelector("#payment_method_id").value = paymentMethod.id;

      form.requestSubmit();
    }
  });

  form.addEventListener("ajax:success", (event) => {
    const [data, _status, _xhr] = event.detail;

    if (spinner) hideSpinner(spinner);
    if (data.error) {
      flashAlert("Erreur de confirmation de paiement : " + data.error);
      submitButton.disabled = false;
    } else if (data.status == "requires_action" && data.redirectUrl) {
      Turbo.visit(data.redirectUrl);
    } else {
      Turbo.visit(data.redirectionUrl);
    }
  });

  form.addEventListener("ajax:error", (event) => {
    const [data, _status, _xhr] = event.detail;
    flashAlert("Erreur de confirmation de paiement : " + data.error);
    if (spinner) hideSpinner(spinner);
    submitButton.disabled = false;
  });
}

/**
 * Handles installment payment form submission.
 * Since no payment is involved for now, user will simply be redirected.
 */
async function initializeInstallmentForm() {
  const form = document.querySelector(".new_payment_form");
  const submitButton = form?.querySelector('button[type="submit"]');
  const spinner = submitButton?.querySelector(".spinner-border");

  submitButton.addEventListener("click", async (e) => {
    e.preventDefault();
    if (spinner) displaySpinner(spinner);
    submitButton.disabled = true;
    form.requestSubmit();
  });

  form?.addEventListener("ajax:success", (event) => {
    if (spinner) hideSpinner(spinner);
    const [data, _status, _xhr] = event.detail;

    if (data.error) {
      flashAlert("Erreur de confirmation de paiement : " + data.error);
      submitButton.disabled = false;
    } else if (data.redirectUrl) {
      Turbo.visit(data.redirectUrl);
    }
  });

  form?.addEventListener("ajax:error", (event) => {
    const [data, _status, _xhr] = event.detail;
    flashAlert("Erreur de confirmation de paiement : " + data.error);
    if (spinner) hideSpinner(spinner);
    if (submitButton) submitButton.disabled = false;
  });
}

/**
 * Initializes Stripe elements.
 * Mounts the card element and sets up a change listener for error display.
 *
 * @returns {Object} An object containing the Stripe instance and the card element.
 */
function initializeStripeElements(form) {
  const stripePublishableKey = document.querySelector(
    "meta[name='stripe-publishable-key']",
  ).content;
  const stripe = Stripe(stripePublishableKey);

  const elements = stripe.elements({ locale: "fr" });
  const cardElement = elements.create("card", { hidePostalCode: true });
  cardElement.mount("#card-element");

  cardElement.addEventListener("change", (event) => {
    const displayError = form.querySelector("#card-errors");
    displayError.textContent = event.error ? event.error.message : "";
  });

  return { stripe, cardElement };
}

/**
 * Displays an alert message in the designated alerts container.
 * Can be use in other parts of the order process to display error messages.
 *
 * @param {String} message - The error message to display.
 */
export function flashAlert(message) {
  let container = document.getElementById("inscription-program-flash-alerts");

  let alertBox = document.createElement("div");
  alertBox.className =
    "alert alert-danger rounded-0 text-center alert-dismissible fade show";
  alertBox.role = "alert";
  alertBox.innerHTML = `
    ${message}
    <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
  `;

  container.appendChild(alertBox);
}
